import { extractErrorMessage } from "../utils/acceptSuggestions";
/**
 * RAMyTasksPage — Asset-owner view of pending/active Risk Assessment tasks.
 *
 * Shown to: process_manager, process_contributor, app_manager, app_contributor,
 *           it_function_manager, it_function_contributor.
 * Asset owner fills the RA form with AI guidance, then submits for manager review.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldAlert, RefreshCw, Sparkles, Send, Save, Loader2, CheckCircle2,
  AlertCircle, Clock, FileText, UserPlus,
} from "lucide-react";
import BPMInviteAppManagerModal from "../components/BPMInviteAppManagerModal";

const STATUS_COLORS = {
  not_started: "bg-slate-100 text-slate-700",
  in_progress: "bg-blue-100 text-blue-700",
  submitted: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  overdue: "bg-red-100 text-red-800 ring-1 ring-red-300",
};

const TREATMENT_OPTIONS = [
  { value: "accept", label: "Accept" },
  { value: "mitigate", label: "Mitigate" },
  { value: "transfer", label: "Transfer" },
  { value: "avoid", label: "Avoid" },
];

export default function RAMyTasksPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiGuidance, setAiGuidance] = useState(null);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/ra-workflow/my-tasks");
      setTasks(res.data.tasks || []);
    } catch (err) {
      toast.error("Failed to load RA tasks");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const openTask = (t) => {
    setSelected(t);
    setForm(t.form || {});
    setAiGuidance(null);
    setOpen(true);
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/ra-workflow/tasks/${selected.id}`, form);
      toast.success("Saved");
      fetchTasks();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (!selected) return;
    await save();
    try {
      await api.post(`/ra-workflow/tasks/${selected.id}/submit`);
      toast.success("Submitted for review");
      setOpen(false);
      fetchTasks();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Submit failed"));
    }
  };

  const getAIGuidance = async () => {
    if (!selected) return;
    setAiLoading(true);
    try {
      const res = await api.post("/ra-workflow/ai-guidance", {
        asset_name: selected.asset_name,
        asset_type: selected.asset_ref_type,
      });
      setAiGuidance(res.data);
      // Pre-fill empty fields with AI suggestions
      setForm((f) => ({
        ...f,
        threat_source: f.threat_source || (res.data.threat_sources || [])[0] || "",
        threat_event: f.threat_event || (res.data.threat_events || [])[0] || "",
        vulnerability: f.vulnerability || (res.data.vulnerabilities || [])[0] || "",
        likelihood: f.likelihood ?? res.data.recommended_likelihood,
        impact: f.impact ?? res.data.recommended_impact,
        treatment: f.treatment || res.data.recommended_treatment,
      }));
      toast.success("AI suggestions loaded");
    } catch (err) {
      toast.error("AI guidance failed");
    } finally {
      setAiLoading(false);
    }
  };

  const summary = tasks.reduce((acc, t) => {
    const s = t.effective_status || t.status;
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <RefreshCw className="w-6 h-6 animate-spin text-sky-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ra-my-tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex gap-2 mb-2">
            <Badge className="bg-amber-100 text-amber-700">PLAN Phase</Badge>
            <Badge variant="outline">ISO 22301 § 8.2.3</Badge>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">My Risk Assessments</h1>
          <p className="text-slate-600 mt-1">
            Complete the Risk Assessment for assets you own. AI-guided and reviewer-approved.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "process_manager" && (
            <Button
              variant="outline"
              onClick={() => setInviteOpen(true)}
              data-testid="bpm-invite-app-manager-trigger"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Invite App Manager
            </Button>
          )}
          <Button variant="outline" onClick={fetchTasks} data-testid="refresh-my-ra">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <BPMInviteAppManagerModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={() => setInviteOpen(false)}
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ["not_started", "Not Started", "bg-slate-50", "text-slate-700"],
          ["in_progress", "In Progress", "bg-blue-50", "text-blue-700"],
          ["submitted", "Submitted", "bg-amber-50", "text-amber-700"],
          ["approved", "Approved", "bg-emerald-50", "text-emerald-700"],
          ["overdue", "Overdue", "bg-red-50", "text-red-700"],
        ].map(([key, label, bg, txt]) => (
          <Card key={key} className={bg}>
            <CardContent className="pt-4">
              <p className="text-xs text-slate-500">{label}</p>
              <p className={`text-2xl font-bold ${txt}`}>{summary[key] || 0}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Task list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" /> Pending Risk Assessments
          </CardTitle>
          <CardDescription>
            Click a row to open the assessment form. AI assistance available.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {tasks.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              No RA tasks assigned. Your BCM Manager will auto-generate tasks after BIA approval.
            </div>
          ) : (
            <div className="divide-y">
              {tasks.map((t) => (
                <div
                  key={t.id}
                  onClick={() => openTask(t)}
                  className="p-4 hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                  data-testid={`ra-task-${t.id}`}
                >
                  <div>
                    <p className="font-medium text-slate-900">{t.asset_name}</p>
                    <p className="text-xs text-slate-500">
                      {t.asset_ref_type} • Due {t.due_date ? new Date(t.due_date).toLocaleDateString() : "—"}
                      {t.days_open > 0 && ` • ${t.days_open}d open`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {t.inherent_risk && (
                      <Badge variant="outline">Inherent: {t.inherent_risk}</Badge>
                    )}
                    <Badge className={STATUS_COLORS[t.effective_status] || STATUS_COLORS.not_started}>
                      {t.effective_status?.replace("_", " ") || "not started"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assessment Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="ra-form-dialog">
          <DialogHeader>
            <DialogTitle>{selected?.asset_name}</DialogTitle>
            <DialogDescription>
              {selected?.asset_ref_type} • Status: {selected?.effective_status || selected?.status}
            </DialogDescription>
          </DialogHeader>

          {/* AI Panel */}
          <div className="bg-gradient-to-r from-sky-50 to-teal-50 border border-sky-200 rounded-lg p-3 my-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-sky-700" />
                <div>
                  <p className="font-semibold text-sm">AI Risk Assistant</p>
                  <p className="text-xs text-slate-600">
                    Get threat / vulnerability / control suggestions for this asset.
                  </p>
                </div>
              </div>
              <Button size="sm" onClick={getAIGuidance} disabled={aiLoading}
                className="bg-sky-700 hover:bg-sky-800 text-white"
                data-testid="ai-guidance-btn">
                {aiLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                {aiGuidance ? "Refresh AI" : "Get AI Suggestions"}
              </Button>
            </div>
            {aiGuidance && (
              <div className="mt-3 grid grid-cols-2 gap-3 text-xs" data-testid="ai-guidance-result">
                <div>
                  <p className="font-semibold">Threat Sources</p>
                  <ul className="list-disc pl-4 text-slate-600">
                    {(aiGuidance.threat_sources || []).slice(0, 3).map((x, i) => <li key={i}>{x}</li>)}
                  </ul>
                </div>
                <div>
                  <p className="font-semibold">Suggested Controls</p>
                  <ul className="list-disc pl-4 text-slate-600">
                    {(aiGuidance.suggested_controls || []).slice(0, 3).map((x, i) => <li key={i}>{x}</li>)}
                  </ul>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Threat Source *</Label>
              <Input data-testid="ra-threat-source"
                value={form.threat_source || ""}
                onChange={(e) => setForm({ ...form, threat_source: e.target.value })}
                placeholder="e.g., Cyber attacker, Natural disaster, Third-party failure" />
            </div>
            <div className="col-span-2">
              <Label>Threat Event *</Label>
              <Input data-testid="ra-threat-event"
                value={form.threat_event || ""}
                onChange={(e) => setForm({ ...form, threat_event: e.target.value })}
                placeholder="e.g., Ransomware encrypts primary data" />
            </div>
            <div className="col-span-2">
              <Label>Vulnerability</Label>
              <Input data-testid="ra-vulnerability"
                value={form.vulnerability || ""}
                onChange={(e) => setForm({ ...form, vulnerability: e.target.value })}
                placeholder="e.g., Insufficient backup frequency" />
            </div>
            <div>
              <Label>Likelihood (1-5) *</Label>
              <Input type="number" min="1" max="5" data-testid="ra-likelihood"
                value={form.likelihood || ""}
                onChange={(e) => setForm({ ...form, likelihood: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Impact (1-5) *</Label>
              <Input type="number" min="1" max="5" data-testid="ra-impact"
                value={form.impact || ""}
                onChange={(e) => setForm({ ...form, impact: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Control Effectiveness (1-5)</Label>
              <Input type="number" min="1" max="5" data-testid="ra-ctrl-eff"
                value={form.control_effectiveness || ""}
                onChange={(e) => setForm({ ...form, control_effectiveness: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Treatment *</Label>
              <Select
                value={form.treatment || ""}
                onValueChange={(v) => setForm({ ...form, treatment: v })}
              >
                <SelectTrigger data-testid="ra-treatment"><SelectValue placeholder="Pick treatment" /></SelectTrigger>
                <SelectContent>
                  {TREATMENT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Residual Likelihood (1-5)</Label>
              <Input type="number" min="1" max="5" data-testid="ra-res-likelihood"
                value={form.residual_likelihood || ""}
                onChange={(e) => setForm({ ...form, residual_likelihood: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Residual Impact (1-5)</Label>
              <Input type="number" min="1" max="5" data-testid="ra-res-impact"
                value={form.residual_impact || ""}
                onChange={(e) => setForm({ ...form, residual_impact: Number(e.target.value) })} />
            </div>
            <div className="col-span-2">
              <Label>Treatment Plan</Label>
              <Textarea rows={3} data-testid="ra-treatment-plan"
                value={form.treatment_plan || ""}
                onChange={(e) => setForm({ ...form, treatment_plan: e.target.value })}
                placeholder="Describe the specific controls/actions to implement" />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} data-testid="ra-notes"
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {form.likelihood && form.impact && (
              <div className="col-span-2 p-3 bg-slate-50 rounded flex justify-between text-sm">
                <span>
                  <strong>Inherent Risk:</strong> {form.likelihood} × {form.impact} = {form.likelihood * form.impact}
                </span>
                {form.residual_likelihood && form.residual_impact && (
                  <span>
                    <strong>Residual:</strong> {form.residual_likelihood * form.residual_impact}
                  </span>
                )}
              </div>
            )}
          </div>

          {selected?.review_history?.length > 0 && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded">
              <p className="font-semibold text-sm text-amber-800 mb-1">Review History</p>
              {selected.review_history.map((h, i) => (
                <div key={i} className="text-xs text-slate-600">
                  <Badge className={h.decision === "approve" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                    {h.decision}
                  </Badge>{" "}
                  by {h.role} on {new Date(h.at).toLocaleString()}
                  {h.comments && <div className="italic mt-1">"{h.comments}"</div>}
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="outline" onClick={save} disabled={saving} data-testid="save-ra-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Draft
            </Button>
            <Button onClick={submit} disabled={saving || selected?.status === "approved"}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-ra-btn">
              <Send className="w-4 h-4 mr-2" /> Submit for Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
